// src/features/dashboard/components/TeamKpiCards.tsx

import {
  useEffect,
} from 'react';

import {
  useTeamKpi,
} from '../hooks/useTeamKpi';

import {
  useLoaderContext,
} from '../../../components/Loader/LoaderContext';


/* ============================================================
   PROPS
============================================================ */

type TeamKpiCardsProps = {
  ManagerEmployeeCode?: string;
};


/* ============================================================
   COMPONENT
============================================================ */

const TeamKpiCards = ({
  ManagerEmployeeCode,
}: TeamKpiCardsProps) => {

  const {
    hideLoader,
  } = useLoaderContext();


  /* ==========================================================
     TEAM KPI HOOK
     
     IMPORTANT:
     
     Date is NOT passed here.
     
     useTeamKpi() gets:
     
       fromDate
       toDate
     
     directly from ManagerDateContext.
     
     Therefore when DashboardHeader changes the date:
     
       ManagerDateContext
             ↓
       useTeamKpi()
             ↓
       API called again
  ========================================================== */

  const {
    kpiCards,
    loading,
    error,
  } = useTeamKpi({
    ManagerEmployeeCode,
  });


  /* ==========================================================
     HIDE GLOBAL LOADER
  ========================================================== */

  useEffect(() => {

    if (
      !loading &&
      (
        kpiCards.length > 0 ||
        Boolean(error)
      )
    ) {

      const timer =
        window.setTimeout(
          () => {
            hideLoader();
          },
          1500,
        );


      return () => {

        window.clearTimeout(
          timer,
        );
      };
    }

    return undefined;

  }, [
    loading,
    kpiCards,
    error,
    hideLoader,
  ]);


  /* ==========================================================
     LOADING
  ========================================================== */

  if (
    loading
  ) {

    return (
      <div
        className="
          w-full
          rounded-[14px]
          border
          border-[#E5E7EB]
          bg-white
          p-5
          text-[14px]
          text-[#667085]
        "
      >
        Loading KPI cards...
      </div>
    );
  }


  /* ==========================================================
     ERROR
  ========================================================== */

  if (
    error
  ) {

    return (
      <div
        className="
          w-full
          rounded-[14px]
          border
          border-[#FECACA]
          bg-[#FEF2F2]
          p-5
        "
      >

        <h3
          className="
            text-[14px]
            font-semibold
            text-[#DC2626]
          "
        >
          KPI Cards Error
        </h3>

        <p
          className="
            mt-1
            text-[12px]
            text-[#7F1D1D]
          "
        >
          {error}
        </p>

      </div>
    );
  }


  /* ==========================================================
     KPI CARDS
  ========================================================== */

  return (
    <div
      className="
        grid
        w-full
        min-w-0
        grid-cols-1
        gap-3

        sm:grid-cols-2

        md:grid-cols-3

        lg:grid-cols-5

        [@media(max-width:1100px)]:gap-2

        [@media(max-width:950px)]:gap-1.5
      "
    >

      {kpiCards.map(
        (
          card,
          index,
        ) => {

          const Icon =
            card.icon;


          return (
            <div
              key={`${card.title}-${index}`}
              className="
                min-w-0
                w-full
                overflow-hidden
                rounded-[14px]
                border
                border-[#E5E7EB]
                bg-white
                px-3
                py-3

                [@media(max-width:1100px)]:rounded-[12px]
                [@media(max-width:1100px)]:px-2.5
                [@media(max-width:1100px)]:py-2.5

                [@media(max-width:950px)]:rounded-[10px]
                [@media(max-width:950px)]:px-2
                [@media(max-width:950px)]:py-2
              "
            >

              <div
                className="
                  flex
                  min-w-0
                  items-center
                  gap-2

                  [@media(max-width:1100px)]:gap-1.5

                  [@media(max-width:950px)]:gap-1
                "
              >

                {/* =====================================================
                    ICON
                ====================================================== */}

                <div
                  className={`
                    flex
                    h-[34px]
                    w-[34px]
                    shrink-0
                    items-center
                    justify-center
                    rounded-full

                    ${card.bg}
                    ${card.color}

                    [@media(max-width:1100px)]:h-[30px]
                    [@media(max-width:1100px)]:w-[30px]

                    [@media(max-width:950px)]:h-[27px]
                    [@media(max-width:950px)]:w-[27px]
                  `}
                >

                  <Icon />

                </div>


                {/* =====================================================
                    CONTENT
                ====================================================== */}

                <div
                  className="
                    min-w-0
                    flex-1
                  "
                >

                  {/* =================================================
                      TITLE
                  ================================================== */}

                  <p
                    className="
                      min-w-0
                      overflow-hidden
                      text-ellipsis
                      whitespace-nowrap

                      text-[11px]
                      font-bold
                      text-[#111827]

                      [@media(max-width:1100px)]:text-[10px]

                      [@media(max-width:950px)]:text-[9px]
                    "
                    title={card.title}
                  >
                    {card.title}
                  </p>


                  {/* =================================================
                      VALUE
                  ================================================== */}

                  <div
                    className="
                      mt-[2px]
                      flex
                      min-w-0
                      items-end
                      gap-1

                      [@media(max-width:1100px)]:gap-0.5

                      [@media(max-width:950px)]:mt-0
                    "
                  >

                    <h2
                      className="
                        min-w-0
                        overflow-hidden
                        text-ellipsis
                        whitespace-nowrap

                        text-[18px]
                        font-bold
                        text-[#111827]

                        [@media(max-width:1100px)]:text-[16px]

                        [@media(max-width:950px)]:text-[14px]
                      "
                    >
                      {card.value}
                    </h2>


                    {/* =================================================
                        SUB VALUE
                    ================================================== */}

                    {card.sub && (

                      <span
                        className="
                          shrink-0

                          text-[10px]
                          text-[#667085]

                          [@media(max-width:1100px)]:text-[9px]

                          [@media(max-width:950px)]:text-[8px]
                        "
                      >
                        {card.sub}
                      </span>

                    )}

                  </div>

                </div>

              </div>

            </div>
          );
        },
      )}

    </div>
  );
};


export default TeamKpiCards;